import { FC, PropsWithChildren, useMemo } from 'react';
import TherapyStyle from './therapyTooltip.module.scss';
import { DateFormats, localFormat } from '../../../helpers/date-helpers';
import Style from '../baseIndicator.module.scss';
import { getIndicatorStyleColor } from '../../../helpers/color-helper';
import { BaseIndicatorWrapper } from '../baseIndicator.component';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { TherapyTooltipProps } from './therapyTooltip';
import clsx from 'clsx';
import { LocalizedResourceDictionaryKeys } from '../../../application/localisation/i18next';
import { useScopedTranslation } from '../../../application/localisation/useScopedTranslation';
import { useCommonLocalization } from '../../../application/localisation/useCommonLocalization';
import { AppButton } from 'uikit/buttons';
import { useTrendModal } from 'src/features/deviceDataTrends/useTrendModal';
import { SummaryCalculatorEnum } from 'src/services/api/api-client';

export const TherapyTooltipComponent: FC<PropsWithChildren<TherapyTooltipProps>> = (
  props: PropsWithChildren<TherapyTooltipProps>,
) => {
  const DEVICE_PARAMETER = SummaryCalculatorEnum.Compliance;

  const { t: tooltipLocalizer } = useScopedTranslation('Dashboard.PatientTable.TherapyTooltip');
  const commonLocalizer = useCommonLocalization();
  const { values, Icon, valueFormatter, tooltipValueText, testId, patient } = props;

  const trends = useTrendModal(patient, DEVICE_PARAMETER, valueFormatter);

  const tooltipHeaderText = useMemo(() => {
    if (
      !props ||
      (!props.indicator?.value && props.indicator?.value !== 0) ||
      props.indicator.deviceIsNotUsedForAggregatedPeriod ||
      props.indicator.therapyIsNotReceivedForAggregatedPeriod ||
      props.indicator.telemonitoringIsDisabled
    ) {
      return '';
    }

    return tooltipLocalizer('TooltipHeader', {
      daysCount: props.indicator.numberOfAggregatedDays,
    });
  }, [props, tooltipLocalizer]);

  const tooltipText = useMemo(() => {
    if (!props) {
      return commonLocalizer('Common_NoData');
    }

    if (props.indicator?.telemonitoringIsDisabled) {
      return tooltipLocalizer('TooltipTelemonitoringIsDisabled');
    }

    if (props.indicator?.therapyIsNotReceivedForAggregatedPeriod) {
      return tooltipLocalizer('TooltipNotReceivedMoreThatThreeDays', {
        postProcess: 'interval',
        count: props?.indicator?.numberOfAggregatedDays,
      });
    }

    if (
      !props.indicator?.therapyIsNotReceivedForAggregatedPeriod &&
      props.indicator?.deviceIsNotUsedForAggregatedPeriod
    ) {
      return tooltipLocalizer('TooltipNotUsed', {
        postProcess: 'interval',
        count: props?.indicator?.numberOfAggregatedDays,
      });
    }

    if (!props.indicator?.value && props.indicator?.value !== 0) {
      return commonLocalizer('Common_NoData');
    }

    return tooltipValueText;
  }, [props, tooltipValueText, tooltipLocalizer, commonLocalizer]);

  const isNullOrEmpty = useMemo(() => {
    return (
      props.indicator?.therapyIsNotReceivedForAggregatedPeriod ||
      props.indicator?.deviceIsNotUsedForAggregatedPeriod ||
      props.indicator?.telemonitoringIsDisabled ||
      !props.indicator
    );
  }, [props.indicator]);

  const listOfParams = useMemo(() => {
    const allParamKeys = values.flatMap((x) => Object.keys(x.values));

    return Array.from(new Set(allParamKeys));
  }, [values]);

  const dailyTable = useMemo(() => {
    return (
      <div>
        <table className={TherapyStyle.therapyTooltip} data-test-id={'therapy-tooltip-table'}>
          <thead>
            <tr>
              <th>{tooltipLocalizer('therapyDate')}</th>
              {values.map((x, index) => {
                return (
                  <th data-test-id={'therapy-tooltip-table-date-column'} key={index}>
                    {localFormat(x.therapyDate, DateFormats.longWithoutYear)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr data-test-id={'therapy-tooltip-table-quality-row'}>
              <td>{tooltipLocalizer('quality')}</td>
              {values.map((x, index) => (
                <td data-test-id={'therapy-tooltip-table-quality-value'} key={index}>
                  <Icon className={clsx(getIndicatorStyleColor(x.quality), TherapyStyle.qualityIconBase)} />
                </td>
              ))}
            </tr>
            {listOfParams.map((param) => (
              <tr key={param} data-test-id={`therapy-tooltip-table-${param}-row`}>
                <td>{tooltipLocalizer(`${param}.caption` as LocalizedResourceDictionaryKeys)}</td>
                {values.map((x) => (
                  <td
                    data-test-id={`therapy-tooltip-table-${param}-value`}
                    key={`${param}-${x.therapyDate.toISOString()}`}
                    className={clsx({
                      [TherapyStyle.emptyValue]: !x.values[param] && x.values[param] !== 0,
                    })}
                  >
                    {x.values[param] || x.values[param] === 0
                      ? valueFormatter?.(x.values[param], param) ??
                        // @ts-ignore
                        tooltipLocalizer(`${param}.value` as LocalizedResourceDictionaryKeys, {
                          value: x.values[param],
                        })
                      : commonLocalizer('Common_dash')}
                  </td>
                ))}
              </tr>
            ))}
            {props.hasTrends && (
              <tr>
                <td>
                  <AppButton
                    variant={'icon-link'}
                    colorSchema={'primary'}
                    text={tooltipLocalizer(
                      `OpenTrendButton.${SummaryCalculatorEnum[DEVICE_PARAMETER]}`,
                      SummaryCalculatorEnum[DEVICE_PARAMETER],
                    )}
                    onClick={trends.open}
                  />
                </td>
                {values.map((x) => (
                  <td key={x.therapyDate.toISOString()} />
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }, [
    tooltipLocalizer,
    values,
    listOfParams,
    props.hasTrends,
    DEVICE_PARAMETER,
    trends.open,
    Icon,
    valueFormatter,
    commonLocalizer,
  ]);

  if (isNullOrEmpty) {
    return (
      <BaseIndicatorWrapper
        tooltipText={tooltipText}
        headerText={tooltipHeaderText}
        isEnabled={false}
        testId={props.testId}
        bottomElement={
          props.hasTrends ? (
            <>
              <AppButton
                variant={'icon-link'}
                colorSchema={'primary'}
                text={tooltipLocalizer(
                  `OpenTrendButton.${SummaryCalculatorEnum[DEVICE_PARAMETER]}`,
                  SummaryCalculatorEnum[DEVICE_PARAMETER],
                )}
                onClick={trends.open}
              />
            </>
          ) : null
        }
      >
        {props.children}
        {trends.modal}
      </BaseIndicatorWrapper>
    );
  }

  return (
    <>
      <Tooltip tooltipContent={dailyTable} calloutContainerStyles={Style.wrapperStyles} testIdPrefix={'therapy'}>
        <div className={Style.container} data-test-id={`indicator-${testId}`}>
          {props.children}
        </div>
      </Tooltip>
      {trends.modal}
    </>
  );
};
